import { PropsWithChildren } from 'react'
import { NavLink, useNavigation, type NavLinkProps } from 'react-router-dom'
import classNames from 'classnames'

type TabNavProps = PropsWithChildren<{ className?: string }>

export default function TabNav({ className, children }: TabNavProps) {
  return (
    <nav
      className={classNames(
        'mb-8 flex flex-wrap gap-x-6 pb-1 text-sm shadow-[inset_0_-1px] shadow-black/10 md:pb-0',
        className,
      )}
    >
      {children}
    </nav>
  )
}

type NavLinkNativeProps = NavLinkProps & React.RefAttributes<HTMLAnchorElement>
type LinkNativeProps = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>
type ButtonNativeProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>

type TabLinkProps =
  | ({ type: 'link' } & LinkNativeProps)
  | ({ type: 'router-link'; className?: string } & Omit<
      NavLinkNativeProps,
      'className'
    >)
  | ({ type: 'button'; isActive?: boolean } & Omit<ButtonNativeProps, 'type'>)

export const tabClassName =
  'pb-2 uppercase underline decoration-transparent transition hover:decoration-primary md:pb-3 md:hover:decoration-transparent md:hover:shadow-[inset_0_-1px] md:hover:shadow-primary'
export const activeTabClassName =
  'cursor-default hover:decoration-transparent md:shadow-[inset_0_-1px] md:shadow-black-drh md:hover:shadow-black-drh'

type TabRouterLinkProps = { type: 'router-link'; className?: string } & Omit<
  NavLinkNativeProps,
  'className'
>

// Split out so useNavigation is only called for the router-link variant --
// the plain-link and button variants have no reason to depend on router
// context at all.
function TabRouterLink(props: TabRouterLinkProps) {
  const { className: customClassName, children, ...rest } = props

  // True while a blocking navigation (route-level `lazy`/`loader`, e.g. every
  // Browse tab) is in flight. During that window the outgoing route is still
  // committed, so its tab reads as `isActive` while the destination reads as
  // `isPending`.
  const navigation = useNavigation()
  const isNavigating = navigation.state !== 'idle'

  return (
    <NavLink
      className={({ isActive, isPending }) => {
        // Highlight as soon as the click registers, rather than lagging until
        // the destination's loaders resolve -- which, for Browse, is the
        // difference between a tab click acknowledging itself and appearing
        // to do nothing. While navigating the pending tab wins and the
        // outgoing one drops, otherwise both would light up at once. Same
        // rule as app/header/header.tsx's HeaderNavLink.
        const highlighted = isPending || (isActive && !isNavigating)

        return classNames(tabClassName, customClassName, {
          [activeTabClassName]: highlighted,
          'text-primary': !highlighted,
        })
      }}
      {...rest}
    >
      {children}
    </NavLink>
  )
}

export function Tab(props: TabLinkProps) {
  switch (props.type) {
    case 'router-link':
      return <TabRouterLink {...props} />

    case 'link': {
      const { className: customClassName, children, ...rest } = props

      return (
        <a
          className={classNames('text-primary', tabClassName, customClassName)}
          {...rest}
        >
          {children}
        </a>
      )
    }

    case 'button': {
      const { isActive, className: customClassName, children, ...rest } = props

      return (
        <button
          className={classNames(tabClassName, customClassName, {
            [activeTabClassName]: isActive,
            'text-primary': !isActive,
          })}
          {...rest}
          type="button"
        >
          {children}
        </button>
      )
    }
  }
}
